/**
 * Edit-tracked form state for the Apprise "Create a virtual machine" form (pure logic).
 *
 * Every patch marks the touched field as user-edited; whenever the context changes (portal
 * environment, region, VM name, size config, options, live catalog) only the values the user
 * has NOT touched are re-derived, and values the new context makes invalid are coerced
 * (an availability option the new region / size does not offer, an unsupported security type,
 * catalog-locked sizing).
 */
import type {
  AppriseAvailability, AppriseBackupPolicy, AppriseBootDiagnostics, AppriseImageRef, AppriseLoadBalancer, AppriseNetwork,
  AppriseOperations, AppriseOsType, AppriseSecurity, AppriseServer, AppriseStorage, AppriseVmConfig, ApprisePublicIp,
  AzureLoadBalancer, AzureNetworkSecurityGroup, AzurePublicIp, AzureStorageAccount, AzureVirtualNetwork,
} from '@/types'
import {
  appriseRegionSupportsZones, buildDefaultAppriseConfig, catalogVmSizing, derivedNames, deriveHostname, readServerDefaults, resourceGroupExists,
  newVirtualNetworkAllowed, takenVnetNames, type AppriseDefaultsContext,
} from './appriseDefaults'
import type { AppriseTagRow } from './appriseTags'
import { tagRowsToRecord } from './appriseTags'

export type AppriseSection = 'server' | 'network' | 'storage' | 'security' | 'operations'
/** Dotted field path, e.g. "server.vm_size" or "network.public_ip.name". */
export type AppriseFieldPath = string

export interface AppriseFormState {
  config: AppriseVmConfig
  edited: Partial<Record<AppriseFieldPath, true>>
  /** The tag editor rows (config.operations.tags is derived from them). */
  tagRows: AppriseTagRow[]
}

export function initAppriseFormState(ctx: AppriseDefaultsContext): AppriseFormState {
  return { config: buildDefaultAppriseConfig(ctx), edited: {}, tagRows: [] }
}

function markEdited(edited: AppriseFormState['edited'], prefix: string, patch: object): AppriseFormState['edited'] {
  const next = { ...edited }
  for (const k of Object.keys(patch)) next[`${prefix}.${k}`] = true
  return next
}

function patchSection<S extends AppriseSection>(state: AppriseFormState, section: S, patch: Partial<AppriseVmConfig[S]>): AppriseFormState {
  return {
    ...state,
    config: { ...state.config, [section]: { ...state.config[section], ...patch } },
    edited: markEdited(state.edited, section, patch),
  }
}

// ─── Derived defaults / coercion ──────────────────────────────────────────────

/** Paths whose untouched value follows the defaults (catalog, environment, VM name, options). */
const DERIVED_PATHS = [
  'server.vm_size', 'server.server_environment', 'storage.os_disk_type', 'storage.os_disk_gb', 'storage.backup_required',
  'security.identity_mode', 'security.credential_mode', 'security.key_vault_mode', 'security.key_vault_resource_group',
  'operations.monitoring_enabled',
  'network.public_ip.mode',
] as const

/** What a T-shirt size decides: picking another size takes these back from the new size (see rederiveAppriseFormState). */
const SIZE_OWNED_PATHS = [
  'server.vm_size', 'storage.os_disk_type', 'storage.os_disk_gb', 'security.identity_mode', 'operations.monitoring_enabled',
  'server.image', 'server.os_type',
] as const

/** Catalog-controlled fields: forced back to the catalog when overrides are not allowed. */
const CATALOG_PATHS = [
  'server.vm_size', 'storage.os_disk_type', 'storage.os_disk_gb', 'security.identity_mode', 'operations.monitoring_enabled',
] as const

function getPath(config: AppriseVmConfig, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (o as Record<string, unknown>)[k], config)
}

function setPath(config: AppriseVmConfig, path: string, value: unknown): void {
  const keys = path.split('.')
  const last = keys.pop() as string
  const target = keys.reduce<Record<string, unknown>>((o, k) => o[k] as Record<string, unknown>, config as unknown as Record<string, unknown>)
  target[last] = value
}

function cloneConfig(c: AppriseVmConfig): AppriseVmConfig {
  return {
    sku: c.sku,
    server: { ...c.server, image: { ...c.server.image }, availability: { ...c.server.availability } },
    network: { ...c.network, public_ip: { ...c.network.public_ip }, load_balancer: { ...c.network.load_balancer } },
    storage: { ...c.storage, backup_policy: { ...c.storage.backup_policy } },
    security: { ...c.security },
    operations: { ...c.operations, boot_diagnostics: { ...c.operations.boot_diagnostics } },
  }
}

function coerceAvailability(config: AppriseVmConfig, ctx: AppriseDefaultsContext): void {
  const a = config.server.availability
  const reset = () => { config.server.availability = { type: 'none', zone: '', availability_set_id: '', vmss_id: '' } }
  if (a.type === 'zone' && !appriseRegionSupportsZones(ctx.region, ctx.options)) return reset()
  const live = ctx.catalog?.availabilityOptions
  if (!live || live.length === 0) return
  const option = live.find((o) => o.id === a.type)
  if (a.type !== 'none' && !option) return reset()
  if (a.type === 'zone' && a.zone && !(option?.zones ?? []).includes(a.zone)) a.zone = ''
  if (a.type === 'availability_set' && a.availability_set_id && !(option?.sets ?? []).some((s) => s.id === a.availability_set_id)) a.availability_set_id = ''
  if (a.type === 'vmss' && a.vmss_id && !(option?.sets ?? []).some((s) => s.id === a.vmss_id)) a.vmss_id = ''
}

/**
 * Re-derive untouched defaults for a changed context while keeping every user-edited field,
 * then coerce values the new context makes invalid.
 */
export function rederiveAppriseFormState(state: AppriseFormState, ctx: AppriseDefaultsContext): AppriseFormState {
  const defaults = buildDefaultAppriseConfig(ctx)
  const edited = { ...state.edited }
  const config = cloneConfig(state.config)
  // Another T-shirt size was picked (the requester went back): its VM, disk, identity, monitoring and default operating
  // system replace the requester's earlier picks, which they can change again. Everything else they typed is kept.
  if (state.config.sku !== ctx.sku) for (const path of SIZE_OWNED_PATHS) delete edited[path]
  // The T-shirt size is never typed in this form: it always follows the size picked in the wizard (the API refuses a
  // request whose apprise_vm.sku differs from the selected size).
  config.sku = ctx.sku

  for (const path of DERIVED_PATHS) {
    if (!edited[path]) setPath(config, path, getPath(defaults, path))
  }
  // The operating system (image) follows the selected size's default image until the requester picks an image.
  if (!edited['server.image'] && !edited['server.os_type']) {
    config.server.image = { ...defaults.server.image }
    config.server.os_type = defaults.server.os_type
  }
  // The backup default follows the (possibly user-edited) server environment.
  if (!edited['storage.backup_required']) {
    config.storage.backup_required = defaults.storage.backup_required
  }

  // Names that embed the VM name / region follow them until the user edits them (new resources only).
  const names = derivedNames(config.server.server_name, ctx.region, takenVnetNames(ctx.catalog?.virtualNetworks, config.server.resource_group_name))
  if (config.network.vnet_mode === 'create_new' && !edited['network.vnet_name']) config.network.vnet_name = names.vnet_name
  if (config.network.subnet_mode === 'create_new' && !edited['network.subnet_name']) config.network.subnet_name = names.subnet_name
  if (config.network.public_ip.mode === 'create_new' && !edited['network.public_ip.name']) config.network.public_ip.name = names.public_ip_name
  if (config.network.nsg_mode === 'create_new' && !edited['network.nsg_name']) config.network.nsg_name = names.nsg_name
  if (!edited['storage.backup_policy.vault_name']) config.storage.backup_policy.vault_name = names.backup_vault_name
  // a vault the server presets keeps its name; otherwise the suggested name follows the VM name
  if (!edited['security.key_vault_name']) config.security.key_vault_name = readServerDefaults(ctx.options.defaults).key_vault?.name ?? names.key_vault_name

  // Always derived, never typed.
  config.server.hostname = deriveHostname(config.server.server_name, config.server.os_type)
  config.server.resource_group_mode = resourceGroupExists(config.server.resource_group_name, ctx.catalog?.resourceGroups) ? 'existing' : 'create_new'
  config.security.trusted_launch = config.security.security_type === 'trusted_launch'

  coerceAvailability(config, ctx)

  const liveSecurity = ctx.catalog?.securityTypes
  if (liveSecurity && liveSecurity.length > 0) {
    const option = liveSecurity.find((o) => o.id === config.security.security_type)
    if (config.security.security_type !== 'standard' && (!option || !option.supported)) {
      config.security.security_type = 'standard'
      config.security.trusted_launch = false
    }
  }

  if (!catalogVmSizing(ctx).overrides_allowed) {
    for (const path of CATALOG_PATHS) {
      setPath(config, path, getPath(defaults, path))
      delete edited[path]
    }
  }
  return { config, edited, tagRows: state.tagRows }
}

// ─── Patchers (every one re-derives afterwards) ───────────────────────────────

export const patchServer = (s: AppriseFormState, p: Partial<AppriseServer>, ctx: AppriseDefaultsContext) =>
  rederiveAppriseFormState(patchSection(s, 'server', p), ctx)
export const patchNetwork = (s: AppriseFormState, p: Partial<AppriseNetwork>, ctx: AppriseDefaultsContext) =>
  rederiveAppriseFormState(patchSection(s, 'network', p), ctx)
export const patchStorage = (s: AppriseFormState, p: Partial<AppriseStorage>, ctx: AppriseDefaultsContext) =>
  rederiveAppriseFormState(patchSection(s, 'storage', p), ctx)
export const patchSecurity = (s: AppriseFormState, p: Partial<AppriseSecurity>, ctx: AppriseDefaultsContext) =>
  rederiveAppriseFormState(patchSection(s, 'security', p), ctx)
export const patchOperations = (s: AppriseFormState, p: Partial<AppriseOperations>, ctx: AppriseDefaultsContext) =>
  rederiveAppriseFormState(patchSection(s, 'operations', p), ctx)

/** Per-key tracking for the backup policy so the vault name keeps following the VM name. */
export function patchBackupPolicy(state: AppriseFormState, patch: Partial<AppriseBackupPolicy>, ctx: AppriseDefaultsContext): AppriseFormState {
  const next: AppriseFormState = {
    ...state,
    config: { ...state.config, storage: { ...state.config.storage, backup_policy: { ...state.config.storage.backup_policy, ...patch } } },
    edited: markEdited(state.edited, 'storage.backup_policy', patch),
  }
  return rederiveAppriseFormState(next, ctx)
}

function patchNested<K extends 'public_ip' | 'load_balancer'>(
  state: AppriseFormState, key: K, patch: Partial<AppriseNetwork[K]>, ctx: AppriseDefaultsContext,
): AppriseFormState {
  const network = { ...state.config.network, [key]: { ...state.config.network[key], ...patch } }
  return rederiveAppriseFormState(
    { ...state, config: { ...state.config, network }, edited: markEdited(state.edited, `network.${key}`, patch) },
    ctx,
  )
}

export const setPublicIp = (s: AppriseFormState, p: Partial<ApprisePublicIp>, ctx: AppriseDefaultsContext) => patchNested(s, 'public_ip', p, ctx)
export const setLoadBalancer = (s: AppriseFormState, p: Partial<AppriseLoadBalancer>, ctx: AppriseDefaultsContext) => patchNested(s, 'load_balancer', p, ctx)

export function setAvailability(state: AppriseFormState, patch: Partial<AppriseAvailability>, ctx: AppriseDefaultsContext): AppriseFormState {
  const availability = { ...state.config.server.availability, ...patch }
  // Exclusivity: only the chosen type keeps its value.
  if (availability.type !== 'zone') availability.zone = ''
  if (availability.type !== 'availability_set') availability.availability_set_id = ''
  if (availability.type !== 'vmss') availability.vmss_id = ''
  return patchServer(state, { availability }, ctx)
}

export function setBootDiagnostics(state: AppriseFormState, patch: Partial<AppriseBootDiagnostics>, ctx: AppriseDefaultsContext): AppriseFormState {
  const boot = { ...state.config.operations.boot_diagnostics, ...patch }
  if (boot.mode !== 'custom') { boot.storage_account = ''; boot.storage_account_resource_group = '' }
  return patchOperations(state, { boot_diagnostics: boot }, ctx)
}

export function setTagRows(state: AppriseFormState, rows: AppriseTagRow[]): AppriseFormState {
  return {
    ...state,
    tagRows: rows,
    config: { ...state.config, operations: { ...state.config.operations, tags: tagRowsToRecord(rows) } },
  }
}

// ─── Explicit actions ─────────────────────────────────────────────────────────

/** The user picks an image from the live list: sets the image and the OS it implies. */
export function chooseImage(
  state: AppriseFormState, image: AppriseImageRef & { os_type: AppriseOsType }, ctx: AppriseDefaultsContext,
): AppriseFormState {
  const ref: AppriseImageRef = { publisher: image.publisher, offer: image.offer, sku: image.sku, version: image.version || 'latest' }
  const patch: Partial<AppriseServer> = { image: ref, os_type: image.os_type }
  return patchServer(state, patch, ctx)
}

/**
 * Picks the admin credential; keeps the Key Vault mode coherent with it.
 *  - existing secret / generated credential: they need a vault (an existing one, or one created for the generated credential)
 *  - between the two password modes: the requester's choice (store in an existing vault, or not) is kept
 *  - from a vault credential to a password: the vault the server presets for the credential when there is one, else none
 *    (the other vault was only meant for reading / generating that credential)
 */
export function chooseCredentialMode(state: AppriseFormState, mode: AppriseSecurity['credential_mode'], ctx: AppriseDefaultsContext): AppriseFormState {
  const { key_vault_mode: kv, credential_mode: previous } = state.config.security
  const isPassword = (m: AppriseSecurity['credential_mode']) => m === 'default_password' || m === 'custom_password'
  const preset = readServerDefaults(ctx.options.defaults).key_vault
  const toPassword = isPassword(mode)
  const fromVault = !isPassword(previous)
  const key_vault_mode: AppriseSecurity['key_vault_mode'] =
    mode === 'existing_key_vault_secret' ? 'existing'
    : mode === 'generate_in_key_vault' ? (kv === 'none' ? 'create_new' : kv)
    : fromVault ? (preset ? 'existing' : 'none')
    : kv === 'existing' ? 'existing' : 'none'
  const presetVault = toPassword && fromVault && preset ? { key_vault_name: preset.name, key_vault_resource_group: preset.resource_group } : {}
  return patchSecurity(state, { credential_mode: mode, key_vault_mode, ...presetVault }, ctx)
}

/** Back to the default new, isolated network: new vnet + subnet with derived names and Azure-style ranges. */
export function selectNewNetwork(state: AppriseFormState, ctx: AppriseDefaultsContext): AppriseFormState {
  const d = buildDefaultAppriseConfig(ctx).network
  // names that skip the virtual networks already in the VM's resource group
  const names = derivedNames(state.config.server.server_name, ctx.region, takenVnetNames(ctx.catalog?.virtualNetworks, state.config.server.resource_group_name))
  const edited = { ...state.edited }
  delete edited['network.vnet_name']
  delete edited['network.subnet_name']
  const next: AppriseFormState = {
    ...state,
    edited,
    config: {
      ...state.config,
      network: {
        ...state.config.network,
        vnet_mode: 'create_new', vnet_name: names.vnet_name, vnet_address_space: d.vnet_address_space, vnet_resource_group: '',
        subnet_mode: 'create_new', subnet_name: names.subnet_name, subnet_prefix: d.subnet_prefix,
      },
    },
  }
  return rederiveAppriseFormState(next, ctx)
}

/** Nothing chosen yet while only existing virtual networks may be used (e.g. the region changed): the requester picks again. */
export function clearExistingNetwork(state: AppriseFormState, ctx: AppriseDefaultsContext): AppriseFormState {
  return patchNetwork(state, {
    vnet_mode: 'existing', vnet_name: '', vnet_resource_group: '', vnet_address_space: '',
    subnet_mode: 'existing', subnet_name: '', subnet_prefix: '',
  }, ctx)
}

/** An existing vnet (and one of its subnets — the first unless given). */
export function selectExistingNetwork(
  state: AppriseFormState, vnet: AzureVirtualNetwork, subnetName: string | undefined, ctx: AppriseDefaultsContext,
): AppriseFormState {
  return patchNetwork(state, {
    vnet_mode: 'existing', vnet_name: vnet.name, vnet_resource_group: vnet.resource_group, vnet_address_space: '',
    subnet_mode: 'existing', subnet_name: subnetName ?? vnet.subnets[0]?.name ?? '', subnet_prefix: '',
  }, ctx)
}

export function selectPublicIp(
  state: AppriseFormState, mode: ApprisePublicIp['mode'], existing: AzurePublicIp | undefined, ctx: AppriseDefaultsContext,
): AppriseFormState {
  if (mode === 'existing') return setPublicIp(state, { mode, name: existing?.name ?? '', resource_group: existing?.resource_group ?? '' }, ctx)
  const edited = { ...state.edited }
  delete edited['network.public_ip.name']
  return setPublicIp({ ...state, edited }, { mode, name: derivedNames(state.config.server.server_name, ctx.region).public_ip_name, resource_group: '' }, ctx)
}

export function selectNsg(
  state: AppriseFormState, mode: AppriseNetwork['nsg_mode'], existing: AzureNetworkSecurityGroup | undefined, ctx: AppriseDefaultsContext,
): AppriseFormState {
  if (mode === 'existing') return patchNetwork(state, { nsg_mode: mode, nsg_name: existing?.name ?? '', nsg_resource_group: existing?.resource_group ?? '' }, ctx)
  const edited = { ...state.edited }
  delete edited['network.nsg_name']
  return patchNetwork({ ...state, edited }, { nsg_mode: mode, nsg_name: derivedNames(state.config.server.server_name, ctx.region).nsg_name, nsg_resource_group: '' }, ctx)
}

export function selectLoadBalancer(state: AppriseFormState, poolId: string, ctx: AppriseDefaultsContext): AppriseFormState {
  return setLoadBalancer(state, poolId ? { mode: 'existing', backend_pool_id: poolId } : { mode: 'none', backend_pool_id: '' }, ctx)
}

export function selectBootStorage(state: AppriseFormState, account: AzureStorageAccount | undefined, ctx: AppriseDefaultsContext): AppriseFormState {
  return setBootDiagnostics(state, {
    mode: 'custom', storage_account: account?.name ?? '', storage_account_resource_group: account?.resource_group ?? '',
  }, ctx)
}

/** The region changed: existing regional resources chosen for the old region no longer apply. */
export function resetRegionalSelections(state: AppriseFormState, ctx: AppriseDefaultsContext): AppriseFormState {
  let s = state
  const c = s.config
  if (c.network.vnet_mode === 'existing') s = newVirtualNetworkAllowed(ctx.options) ? selectNewNetwork(s, ctx) : clearExistingNetwork(s, ctx)
  if (c.network.public_ip.mode === 'existing') s = selectPublicIp(s, 'create_new', undefined, ctx)
  if (c.network.nsg_mode === 'existing') s = selectNsg(s, 'create_new', undefined, ctx)
  if (c.network.load_balancer.mode === 'existing') s = selectLoadBalancer(s, '', ctx)
  if (c.operations.boot_diagnostics.mode === 'custom') s = setBootDiagnostics(s, { mode: 'managed' }, ctx)
  if (c.server.availability.type === 'availability_set' || c.server.availability.type === 'vmss')
    s = setAvailability(s, { type: 'none' }, ctx)
  return s
}

/** Helper for lists: the existing load balancer owning a backend pool id. */
export function loadBalancerOfPool(lbs: AzureLoadBalancer[], poolId: string): AzureLoadBalancer | undefined {
  return lbs.find((lb) => lb.backend_pools.some((p) => p.id === poolId))
}
